import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, ExternalLink, ChevronLeft, ChevronRight, FileText, ZoomIn, ZoomOut } from "lucide-react";

const cache = new Map();
const MISSING_PDF = "Original PDF not stored for this document — re-upload the PDF to enable page view.";

// Fetches (and caches) a rendered page image with optional citation highlight.
function usePageImage({ docId, cur, page, highlight }) {
  const [state, setState] = useState({ img: null, err: "", loading: false, hits: null });
  useEffect(() => {
    if (!docId) return;
    const q = cur === (page || 1) && highlight ? highlight.slice(0, 400) : "";
    const key = `${docId}:${cur}:${q}`;
    if (cache.has(key)) { const c = cache.get(key); setState({ img: c.url, err: "", loading: false, hits: c.hits }); return; }
    let cancelled = false;
    setState((s) => ({ ...s, err: "", loading: true }));
    api.get(`/documents/${docId}/page/${cur}.png`, { responseType: "blob", params: q ? { q } : {} })
      .then((r) => {
        const c = { url: URL.createObjectURL(r.data), hits: q ? Number(r.headers["x-highlight-hits"] ?? 0) : null };
        cache.set(key, c);
        if (!cancelled) setState({ img: c.url, err: "", loading: false, hits: c.hits });
      })
      .catch((e) => { if (!cancelled) setState({ img: null, hits: null, loading: false, err: e?.response?.status === 404 ? MISSING_PDF : "Could not render page" }); });
    return () => { cancelled = true; };
  }, [docId, cur, page, highlight]);
  return state;
}

const iconBtn = "text-slate-400 hover:text-amber-400";

function PdfToolbar({ docName, cur, setCur, pageCount, setZoom, docId, onClose }) {
  const openPdf = async () => {
    const r = await api.get(`/documents/${docId}/file`, { responseType: "blob" });
    window.open(`${URL.createObjectURL(r.data)}#page=${cur}`, "_blank");
  };
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 flex-wrap">
      <FileText className="w-4 h-4 text-amber-400 shrink-0" />
      <div className="text-xs text-slate-200 truncate flex-1" data-testid="pdf-panel-doc-name">{docName}</div>
      <button onClick={() => setCur((c) => Math.max(1, c - 1))} className={iconBtn} data-testid="pdf-prev-page"><ChevronLeft className="w-4 h-4" /></button>
      <input type="number" min={1} max={pageCount || 9999} value={cur} onChange={(e) => setCur(Math.max(1, Number(e.target.value) || 1))} className="w-14 bg-slate-950/60 border border-slate-800 rounded px-1 py-0.5 text-xs font-mono text-center" data-testid="pdf-page-input" />
      <span className="text-xs font-mono text-slate-500">/ {pageCount || "?"}</span>
      <button onClick={() => setCur((c) => Math.min(pageCount || c + 1, c + 1))} className={iconBtn} data-testid="pdf-next-page"><ChevronRight className="w-4 h-4" /></button>
      <span className="w-px h-4 bg-slate-800 mx-1" />
      <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className={iconBtn} data-testid="pdf-zoom-out"><ZoomOut className="w-4 h-4" /></button>
      <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))} className={iconBtn} data-testid="pdf-zoom-in"><ZoomIn className="w-4 h-4" /></button>
      <button onClick={openPdf} className={iconBtn} title="Open full PDF in new tab" data-testid="pdf-open-new-tab"><ExternalLink className="w-4 h-4" /></button>
      <button onClick={onClose} className="text-slate-400 hover:text-red-400" data-testid="pdf-panel-close"><X className="w-4 h-4" /></button>
    </div>
  );
}

function HighlightBadge({ hits }) {
  const found = hits > 0;
  return (
    <div className={`absolute top-2 left-2 z-10 px-2 py-1 rounded text-[10px] font-mono border ${found ? "bg-amber-500/20 border-amber-500/50 text-amber-300" : "bg-slate-800/80 border-slate-700 text-slate-400"}`} data-testid="pdf-highlight-status">
      {found ? `${hits} highlight${hits > 1 ? "s" : ""} — cited text` : "Cited text not found on this page"}
    </div>
  );
}

export function PdfPanel({ docId, docName, page, pageCount, highlight, onClose }) {
  const [cur, setCur] = useState(page || 1);
  const [zoom, setZoom] = useState(1);
  useEffect(() => { setCur(page || 1); }, [page, docId, highlight]);
  const { img, err, loading, hits } = usePageImage({ docId, cur, page, highlight });
  return (
    <div className="sc-card flex flex-col fixed inset-0 z-[60] h-[100dvh] rounded-none lg:static lg:z-auto lg:h-[calc(100vh-8rem)] lg:sticky lg:top-6 lg:rounded-[10px]" data-testid="pdf-panel">
      <PdfToolbar docName={docName} cur={cur} setCur={setCur} pageCount={pageCount} setZoom={setZoom} docId={docId} onClose={onClose} />
      <div className="flex-1 bg-slate-950 relative overflow-auto">
        {loading && <div className="absolute inset-0 flex items-center justify-center text-xs font-mono text-amber-400 animate-pulse z-10">Rendering page {cur}…</div>}
        {err && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-xs text-red-400" data-testid="pdf-panel-error">{err}</div>}
        {hits !== null && !loading && <HighlightBadge hits={hits} />}
        {img && <img src={img} alt={`Page ${cur}`} style={{ width: `${zoom * 100}%` }} className={`block mx-auto ${loading ? "opacity-40" : ""}`} data-testid="pdf-page-image" />}
      </div>
    </div>
  );
}
